<!--
Author: Alexey Usov (dax@xdax.ru, https://t.me/doubleaxe, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
-->
<script setup lang="ts">
import {computed, ref, watch, unref} from 'vue';
import type {RecipeIOModel} from '@/scripts/model/store';
import {formatIo} from '@/scripts/format';
import {injectFilter} from '@/scripts/filter';
import {SelectedClassType, useLinkDragAndDrop, LinkDragAndDropItem, usePointAndClick} from '@/composables/drag-helpers';
import {mdiArrowDown, mdiArrowLeft, mdiArrowRight, mdiArrowUp} from '@mdi/js';

const props = defineProps<{
    io: RecipeIOModel;
    //fork addition: which card edge this port sits on, used to orient
    //the flow arrow on the port badge ('top'/'bottom' come from the
    //TB/BT flow orientations)
    side?: 'left' | 'right' | 'top' | 'bottom';
    //fork addition: 'strip' renders a compact one-line chip for the
    //top/bottom edge strips of TB/BT cards; default is the tall row
    layout?: 'row' | 'strip';
}>();
const emit = defineEmits<{
    (e: 'text-update'): void;
}>();

const filter = injectFilter();
const {dragStart} = useLinkDragAndDrop();
const {selectedItem, selectItem} = usePointAndClick();

//fork addition: the product icon and its direction arrow (port badge)
//are ONE combined interactive unit — hovering either highlights both
//as a pair, and a drag starting on either begins the link drag
const pairHover = ref(false);

const highlightBorderSide = () => {
    switch(props.io.highlightBorder) {
        case 1: return 'highlight-upper-border';
        case -1: return 'highlight-lower-border';
        default: return undefined;
    }
};
const computedIconClass = computed(() => {
    const _highlightBorder = highlightBorderSide();
    const cls = [
        _highlightBorder || (unref(selectedItem)?.isSelected(props.io) ? 'selected-border' : undefined),
    ].filter(Boolean);
    return cls;
});
const computedDescriptionClass = computed(() => {
    const causesSolvingError = props.io.causesSolvingError ? 'bg-error' : '';
    const cls = [
        isLtr() ? 'text-left' : 'text-right',
        causesSolvingError || (props.io.hasProbability ? 'bg-has-probability' : undefined),
        ((filter.key == props.io.name) ? 'highlight-border' : undefined),
    ].filter(Boolean);
    return cls;
});

function isLtr() {
    return props.io.isFlipped ? !props.io.isInput : props.io.isInput;
}
//fork addition: the badge arrow always points in the direction of
//material flow — into the card for inputs, out of the card for outputs,
//mirrored by the edge the port sits on
const portArrowIcon = computed(() => {
    const side = props.side || 'left';
    if(side === 'top')
        return props.io.isInput ? mdiArrowDown : mdiArrowUp;
    if(side === 'bottom')
        return props.io.isInput ? mdiArrowUp : mdiArrowDown;
    const pointsRight = (side == 'left') === props.io.isInput;
    return pointsRight ? mdiArrowRight : mdiArrowLeft;
});
const direction = computed(() => (props.layout === 'strip') ? 'flex-row' : (isLtr() ? 'flex-row' : 'flex-row-reverse'));
const cpsSolvedTotal = computed(() => formatIo(props.io.cpsSolvedTotal, props.io) || '?');
const cpsMaxTotal = computed(() => formatIo(props.io.cpsMaxTotal, props.io));
//fork fix: the max rate is noise when it equals the solved rate (the
//usual case) — only show it when it actually differs
const showMaxTotal = computed(() => {
    const max = unref(cpsMaxTotal);
    return !!max && (max !== unref(cpsSolvedTotal));
});

function filterForIo() {
    if(filter.key != props.io.name) {
        filter.key = props.io.name;
        filter.direction = props.io.isInput ? 1 : -1;
    } else {
        filter.key = undefined;
    }
}

function newLinkDragAndDropItem() {
    //if used inline, as @pointerdown.left="dragStart($event, new LinkDragAndDropItem(props.io))"
    //it will be transformed to new unref(LinkDragAndDropItem)(props.io) which causes bug
    return new LinkDragAndDropItem(props.io);
}

//fork addition: the whole io row is a link-drag handle, not just the
//badge and icon. Presses on the name/rates start the drag lazily —
//only once the pointer moves past the click threshold — so a plain
//click on the name still reaches its filter click handler and never
//turns into an accidental link drop. dragStart needs the activator
//element and coordinates; the press event's currentTarget is gone by
//the time the threshold is crossed, so both are captured up front.
function onRowPointerDown(event: PointerEvent) {
    event.stopPropagation();
    const activator = event.currentTarget as HTMLElement;
    const startX = event.clientX;
    const startY = event.clientY;
    const onMove = (moveEvent: PointerEvent) => {
        if(Math.max(Math.abs(moveEvent.clientX - startX), Math.abs(moveEvent.clientY - startY)) <= 6)
            return;
        cleanup();
        dragStart({
            currentTarget: activator,
            clientX: moveEvent.clientX,
            clientY: moveEvent.clientY,
        } as unknown as PointerEvent, newLinkDragAndDropItem());
    };
    const cleanup = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', cleanup);
        window.removeEventListener('pointercancel', cleanup);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', cleanup);
    window.addEventListener('pointercancel', cleanup);
}

watch([() => props.io.cpsSolvedTotal, () => props.io.cpsMaxTotal], () => emit('text-update'));
</script>

<template>
    <div
        class="io-parent"
        :class="[direction, (props.layout === 'strip') ? 'io-strip-chip' : undefined]"
        @pointerdown.left="onRowPointerDown($event)"
    >
        <span
            class="port-badge"
            :class="[props.io.isInput ? 'port-in' : 'port-out', {'pair-hover': pairHover}]"
            :title="props.io.isInput ? 'Input — drag onto a matching output' : 'Output — drag onto a matching input'"
            @pointerdown.left.stop="dragStart($event, newLinkDragAndDropItem())"
            @mouseenter="pairHover = true"
            @mouseleave="pairHover = false"
        >
            <v-icon :icon="portArrowIcon" size="14" />
        </span>
        <icon-component
            class="io-icon-row rounded hover-elevation"
            :class="[computedIconClass, {'pair-hover': pairHover}]"
            :image="props.io.image"
            :data-tooltip="props.io.label"
            :data-io-id="props.io.key"
            @click="selectItem(SelectedClassType.RecipeIOModel, props.io)"
            @pointerdown.left.stop="dragStart($event, newLinkDragAndDropItem())"
            @click.stop
            @mouseenter="pairHover = true"
            @mouseleave="pairHover = false"
        />
        <div
            v-if="(props.layout === 'strip')"
            class="io-strip-text text-caption"
            @click="filterForIo()"
        >
            <span class="io-product-name">{{ props.io.label }}</span>
            <span class="io-strip-rate">{{ cpsSolvedTotal }}</span>
        </div>
        <div
            v-else
            class="io-description-row text-caption hover-border"
            :class="computedDescriptionClass"
            @click="filterForIo()"
        >
            <span class="io-product-name">{{ props.io.label }}</span>
            <br>
            {{ cpsSolvedTotal }}
            <template v-if="showMaxTotal">
                <br>
                {{ cpsMaxTotal }}
            </template>
        </div>
    </div>
</template>

<style scoped>
.io-parent {
    flex-wrap: nowrap;
    display: flex;
    align-items: center;
    /* fork addition: generous hit area — the whole row starts link
       drags, so give it real height and padding to land on */
    min-height: 30px;
    padding: 2px 4px;
    box-sizing: border-box;
    cursor: grab;
}
/* fork addition: compact one-line chip used in the top/bottom strips
   of TB/BT cards */
.io-strip-chip {
    min-height: 30px;
    border-radius: 4px;
}
.io-strip-text {
    display: flex;
    align-items: baseline;
    gap: 4px;
    padding-left: 2px;
    white-space: nowrap;
}
.io-strip-rate {
    opacity: 0.75;
}
/* fork addition: unmistakable port handle — colored circular badge with a
   flow arrow, pinned to the card edge (first in DOM; the row direction
   logic mirrors it to the correct side) */
.port-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    color: #fff;
    cursor: grab;
}
.port-in {
    background-color: #00897b;
}
.port-out {
    background-color: #ef6c00;
}
/* fork addition: combined icon+arrow pair highlight — hovering either
   half of the port lights up both, in every orientation */
.port-badge.pair-hover {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(38, 50, 56, 0.55);
    transform: scale(1.15);
}
.io-icon-row.pair-hover {
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgba(38, 50, 56, 0.55);
    transform: scale(1.08);
}
.io-icon-row {
    display: block;
    /* fork addition: the product icon is the drag handle for linking */
    cursor: grab;
}
.io-product-name {
    display: inline-block;
    max-width: 72px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
    font-weight: 600;
}
.io-description-row {
    display: block;
    padding-left: 4px;
    padding-right: 4px;
    flex: 1;
}
</style>
